import {
  LogisticElementViewModel,
  OperationBalanceConfiguration,
  CapacityModel,
  AllocationModel,
  AddendumModel
} from './capacity.model'
import {
  Capacidad,
  ElementoLogistico,
  ConfigOperacionBalance,
  Reparto,
  RepartoPatch,
  EnumLockCapacity,
  LogisticElement,
  EnumProductCapacityType,
  EnumNatureType,
  EnumServiceEnum
} from '../../api'

import {
  EnumBloqueoCapacidad,
  EnumTipoOperacion,
  EnumSubTipoElemLog,
  EnumZonaBalance,
  EnumOperationState,
  EnumDispatchType,
  EnumEstadoProductoCapacidad,
  EnumTipoNaturaleza,
  EnumServicio,
  EnumTipoProductoCapacidad
} from '../../api/oData/enums'

import {
  enumKeyToValue,
  enumValueToKey,
  toLocaleDate,
  enumEnumToValueNumber,
  enumTranslateOption
} from '../../helpers'
import { DEFAULT_DECIMAL_POSITIONS, NO_DATA } from '../../model/constants'
import { DecimalScale, SelectOption, UserPermission } from '@globaleris/ui.library'
import { OperationRequest, CapacityRequest, DispatchRequest } from '../../api/oData/requests'
import {
  getGlobalActionsConfiguration,
  getSecondaryActionsConfiguration,
  getGlobalAllocationActionsConfiguration,
  canEditAllocation
} from './capacity.service'
import { formatLocaleNumber, formatLocalePercentage } from '../../helpers/numberHelper'
import i18n from '../../i18n'
import { hasPermission } from '../../helpers/permissionsHelper'
import { Resources } from '../../model/RBAC/constants'
import { getActiveBalanceZone } from '../../redux'
import { itemsDestinations } from '../tanks/tanks.mapper'
import { Capacity, Addendum } from '../../api/Pioneer/model/Capacity'

export const mapCapacityListFromApi = (items: Capacity[]): CapacityModel[] => {
  return items && items.map(mapCapacityFromApi)
}

export const mapCapacityFromApi = (item: Capacity): CapacityModel => {
  return (
    item && {
      id: item.id,
      lock: enumKeyToValue(EnumLockCapacity, item.lock),
      lockState: enumKeyToValue(EnumLockCapacity, item.lock),
      initDate: item.initDate,
      formattedInitDate: item.initDate ? toLocaleDate(item.initDate) : NO_DATA,
      endDate: item.endDate,
      formattedEndDate: item.endDate ? toLocaleDate(item.endDate) : NO_DATA,
      code: item.codProductCapacity,
      service: enumKeyToValue(EnumServiceEnum, item.contractType),
      translatedService: i18n.t(`enums:EnumServiceEnum.${item?.contractType}`),
      parentProduct: item.parentProduct,
      logisticElement: item.logisticElement,
      nature: enumKeyToValue(EnumNatureType, item.natureType),
      translatedNature: i18n.t(`enums:EnumNatureType.${item?.natureType}`),
      duration: enumKeyToValue(EnumProductCapacityType, item.productCapacityType),
      translatedDuration: enumTranslateOption(
        item.productCapacityType,
        EnumTipoProductoCapacidad,
        'enums:EnumTipoProductoCapacidad'
      ),
      capacity: item.qdContracted,
      formattedCapacity: item.qdContracted
        ? formatLocaleNumber(item.qdContracted, DecimalScale.ENERGY)
        : NO_DATA,
      premium: item.bonus,
      formattedPremium: item.bonus ? formatLocaleNumber(item.bonus, DecimalScale.ENERGY) : NO_DATA,
      signDate: item.signDate,
      formattedSignDate: item.signDate ? toLocaleDate(item.signDate) : NO_DATA,
      rawMaterial: item.rawMaterial,
      hoursQty: item.nmHours,
      formattedRawMaterial: item.rawMaterial ? (item.rawMaterial ? 'Sí' : 'No') : NO_DATA,
      productCapacityState: enumKeyToValue(EnumEstadoProductoCapacidad, item.statusProductCapacity),
      actionsConfiguration: getGlobalActionsConfiguration(
        hasPermission(Resources.Nomination, [UserPermission.EDIT])
      ),
      secondaryActionsConfiguration: getSecondaryActionsConfiguration(),
      addendums: [],
      canViewFormalized:
        enumKeyToValue(EnumEstadoProductoCapacidad, item.statusProductCapacity) ===
        EnumEstadoProductoCapacidad.Formalizada
    }
  )
}

export const mapCapacityAllocationFromApi = (
  item: Capacity,
  allocations: Reparto[]
): CapacityModel => {
  const contractTypeFormatted =
    typeof item.contractType === 'number'
      ? item.contractType
      : enumEnumToValueNumber(EnumServiceEnum, item.contractType)
  return (
    item && {
      id: item.id,
      code: item.codProductCapacity,
      initDate: item.initDate,
      formattedInitDate: item.initDate ? toLocaleDate(item.initDate) : NO_DATA,
      endDate: item.endDate,
      formattedEndDate: item.endDate ? toLocaleDate(item.endDate) : NO_DATA,
      service: enumKeyToValue(EnumServiceEnum, item.contractType),
      translatedService: i18n.t(
        `enums:EnumServicio.${enumKeyToValue(EnumServicio, contractTypeFormatted)}`
      ),
      logisticElement: mapLogisticElement(item.logisticElement),
      nature: enumKeyToValue(EnumNatureType, item.natureType),
      translatedNature: i18n.t(
        `enums:EnumTipoNaturaleza.${enumKeyToValue(EnumTipoNaturaleza, enumEnumToValueNumber(EnumNatureType, item.natureType))}`
      ),
      duration: enumKeyToValue(EnumProductCapacityType, item.productCapacityType),
      translatedDuration: enumTranslateOption(
        item.productCapacityType,
        EnumTipoProductoCapacidad,
        'enums:EnumTipoProductoCapacidad'
      ),
      capacity: item.qdContracted,
      formattedCapacity: item.qdContracted
        ? formatLocaleNumber(item.qdContracted, DecimalScale.ENERGY)
        : NO_DATA,
      signDate: item.signDate,
      hoursQty: item.nmHours,
      formattedSignDate: item.signDate ? toLocaleDate(item.signDate) : NO_DATA,
      premium: item.bonus,
      formattedPremium: item.bonus ? formatLocaleNumber(item.bonus, DecimalScale.ENERGY) : NO_DATA,
      rawMaterial: item.rawMaterial,
      formattedRawMaterial: item.rawMaterial ? (item.rawMaterial ? 'Sí' : 'No') : NO_DATA,
      lock: parseLock(item.lock),
      lockState: enumValueToKey(EnumLockCapacity, item.lock),
      allocations: mapAllocationListFromApi(
        allocations,
        enumValueToKey(EnumBloqueoCapacidad, item.lock),
        item.qdContracted
      ),
      addendums: item?.addendum.map(mapAddendumToAddendumModel) || []
    }
  )
}

export const mapAddendumToAddendumModel = (item: Addendum): AddendumModel => {
  return (
    item && {
      id: item.id,
      productCapacityId: item.productCapacityId,
      quantity: item.quantity,
      initDate: item.initDate,
      formattedInitDate: item.initDate ? toLocaleDate(item.initDate) : NO_DATA,
      endDate: item.endDate,
      formattedEndDate: item.endDate ? toLocaleDate(item.endDate) : NO_DATA,
      signDate: item.signDate,
      formattedSignDate: item.signDate ? toLocaleDate(item.signDate) : NO_DATA,
      bonusDiff: item.bonusDiff
    }
  )
}

export const mapCapacityPOST = (
  item: CapacityModel,
  balanceZoneId: number | undefined,
  companyId: number | undefined
): CapacityRequest => {
  return (
    item && {
      codProductoCapacidad: item.code,
      tipoContrato: enumValueToKey(EnumServicio, item.service),
      tipoNaturaleza: enumValueToKey(EnumTipoNaturaleza, item.nature),
      tipoProductoCapacidad: enumValueToKey(EnumTipoProductoCapacidad, item.duration),
      qdContratada: item.capacity,
      prima: item.premium,
      bloqueo: item.lock
        ? enumKeyToValue(EnumBloqueoCapacidad, EnumBloqueoCapacidad.BloqueadoUsuario)
        : enumKeyToValue(EnumBloqueoCapacidad, EnumBloqueoCapacidad.Libre),
      materiaPrima: item.rawMaterial,
      fechaInicio: item.initDate,
      fechaFin: item.endDate,
      fechaFirma: item.signDate,
      estadoProductoCapacidad: enumValueToKey(
        EnumEstadoProductoCapacidad,
        EnumEstadoProductoCapacidad.Borrador
      ),
      elementoLogistico: {
        '@odata.id': 'elementoslogisticos(' + item.logisticElement?.id + ')'
      },
      empresa: {
        '@odata.id': 'empresa(' + companyId + ')'
      },
      zonaBalance: enumValueToKey(EnumZonaBalance, balanceZoneId),
      nmHoras: item.hoursQty
    }
  )
}

export const mapCapacityPATCH = (item: CapacityModel): CapacityRequest => {
  return (
    item && {
      id: item.id,
      codProductoCapacidad: item.code,
      tipoContrato: enumValueToKey(EnumServicio, item.service),
      tipoProductoCapacidad: enumValueToKey(EnumTipoProductoCapacidad, item.duration),
      tipoNaturaleza: enumValueToKey(EnumTipoNaturaleza, item.nature),
      qdContratada: item.capacity,
      prima: item.premium,
      bloqueo: enumValueToKey(EnumBloqueoCapacidad, item.lockState),
      materiaPrima: item.rawMaterial,
      fechaInicio: item.initDate,
      fechaFin: item.endDate,
      fechaFirma: item.signDate,
      nmHoras: item.hoursQty
    }
  )
}

export const mapAllocationPATCH = (item: AllocationModel): RepartoPatch => {
  return (
    item && {
      id: item.id,
      cantidad: item.quantity
    }
  )
}

export const mapAllocationListFromApi = (
  items: Reparto[],
  lockState: EnumBloqueoCapacidad,
  quantity?: number
): AllocationModel[] => {
  return items && items.map(item => mapAllocationFromApi(item, lockState, quantity))
}

export const mapAllocationFromApi = (
  item: Reparto,
  lockState: EnumBloqueoCapacidad,
  quantity?: number
): AllocationModel => {
  return (
    item && {
      id: item.id,
      capacityQuantity: quantity,
      quantity: item.cantidad,
      percentage: mapCalculatePercent(item.cantidad! / quantity!),
      quantitySent: item.cantidadEnvio,
      quantityTSO: item.cantidadTSO,
      quantityView:
        item.cantidad !== null
          ? `${formatLocaleNumber(item.cantidad, DecimalScale.ENERGY)} (${formatLocalePercentage(
              item.cantidad! / quantity!
            )}%)`
          : '',
      quantitySentView:
        item.cantidadEnvio !== null
          ? `${formatLocaleNumber(
              item.cantidadEnvio,
              DecimalScale.ENERGY
            )} (${formatLocalePercentage(item.cantidadEnvio! / quantity!)}%)`
          : '',
      quantityTSOView:
        item.cantidadTSO !== null
          ? `${formatLocaleNumber(item.cantidadTSO, DecimalScale.ENERGY)} (${formatLocalePercentage(
              item.cantidadTSO! / quantity!
            )}%)`
          : '',
      date: item.operacion?.fechaOperacion,
      daily: 1,
      operationId: item.operacion?.id,
      operationType: enumValueToKey(EnumTipoOperacion, item.operacion?.tipoOperacion),
      operationState: enumValueToKey(EnumOperationState, item.operacion?.estadoOperacion),
      actionsConfiguration: getGlobalAllocationActionsConfiguration(),
      canEdit: canEditAllocation(item.operacion?.fechaOperacion!, lockState),
      operationLockState: lockState
    }
  )
}

export const mapLogisticElement = (logisticElement: LogisticElement): LogisticElementViewModel => {
  return logisticElement && { id: logisticElement.id, description: logisticElement.description }
}

export const mapLogisticElementFromElementoLogistico = (
  logisticElement: ElementoLogistico
): LogisticElementViewModel => {
  return logisticElement && { id: logisticElement.id, description: logisticElement.descripcion }
}

export const parseLock = (lockState: EnumLockCapacity | undefined): boolean => {
  if (enumKeyToValue(EnumBloqueoCapacidad, lockState) === EnumBloqueoCapacidad.Libre) {
    return false
  } else {
    return true
  }
}

export const mapLogisticElementToDropDown = (items: ElementoLogistico[]): SelectOption[] => {
  return items && items.map(logisticElementMapperToDropDown)
}

export const logisticElementMapperToDropDown = (item: ElementoLogistico): SelectOption => {
  return (
    item && {
      value: item.id,
      label: item.descripcion || ''
    }
  )
}

export const mapConfigOperationBalanceListFromApi = (
  items: ConfigOperacionBalance[]
): OperationBalanceConfiguration[] => {
  return items && items.map(mapConfigOperationBalanceFromApi)
}

export const mapConfigOperationBalanceFromApi = (
  item: ConfigOperacionBalance
): OperationBalanceConfiguration => {
  return (
    item && {
      operationType: enumKeyToValue(EnumTipoOperacion, item.tipoOperacion),
      logisticElementSubType: enumKeyToValue(EnumSubTipoElemLog, item.subtipoElementoLogistico)
    }
  )
}

export const mapCalculatePercent = (
  value: number,
  decimalPositions: number = DEFAULT_DECIMAL_POSITIONS
): number => {
  return +(value * 100).toFixed(decimalPositions)
}

export const mapCapacityStatePATCH = (item: AllocationModel | undefined): OperationRequest => {
  return {
    id: item?.operationId,
    tipoOperacion: enumKeyToValue(EnumTipoOperacion, item?.operationType),
    estadoOperacion: enumKeyToValue(EnumOperationState, item?.operationState)
  }
}

export const mapDispatchAllocationPOST = (
  items: AllocationModel[],
  typeDispatch: EnumDispatchType,
  idCompany: number
): DispatchRequest => {
  return (
    items && {
      dispatchType: enumKeyToValue(EnumDispatchType, typeDispatch),
      companyID: idCompany,
      operations: items.map(item => {
        return { id: item.id, balanceZone: getActiveBalanceZone()?.code! }
      })
    }
  )
}
